import { useEffect, useRef, useState, useCallback } from "react";

interface InteractiveGridBackgroundProps {
  cellSize?: number;
  activeColor?: string;
  borderColor?: string;
}

const InteractiveGridBackground = ({
  cellSize = 60,
  activeColor = "rgba(155, 135, 245, 0.06)",
  borderColor = "rgba(148, 163, 184, 0.04)",
}: InteractiveGridBackgroundProps) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // On mobile, use a pure CSS grid background — zero DOM nodes
  if (isMobile) {
    return (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${borderColor} 1px, transparent 1px), linear-gradient(90deg, ${borderColor} 1px, transparent 1px)`,
          backgroundSize: `${cellSize}px ${cellSize}px`,
        }}
      />
    );
  }

  return <DesktopGrid cellSize={cellSize} activeColor={activeColor} borderColor={borderColor} />;
};

/** Desktop-only interactive grid using canvas for performance */
const DesktopGrid = ({
  cellSize,
  activeColor,
  borderColor,
}: {
  cellSize: number;
  activeColor: string;
  borderColor: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCellRef = useRef<{ x: number; y: number } | null>(null);
  const frameRef = useRef<number>(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const cols = Math.ceil(w / cellSize);
    const rows = Math.ceil(h / cellSize);
    const active = activeCellRef.current;

    // Draw grid lines
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    for (let c = 0; c <= cols; c++) {
      const x = c * cellSize;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let r = 0; r <= rows; r++) {
      const y = r * cellSize;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // Highlight active cells
    if (active) {
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const col = active.x + dc;
          const row = active.y + dr;
          if (col < 0 || row < 0 || col >= cols || row >= rows) continue;
          const isCenter = dr === 0 && dc === 0;
          ctx.fillStyle = isCenter
            ? activeColor
            : activeColor.replace("0.06", "0.03");
          ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
        }
      }
    }
  }, [cellSize, activeColor, borderColor]);

  useEffect(() => {
    draw();
    const handleResize = () => draw();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.floor((e.clientX - rect.left) / cellSize);
      const y = Math.floor((e.clientY - rect.top) / cellSize);
      activeCellRef.current = { x, y };
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(draw);
    }
  };

  const handleMouseLeave = () => {
    activeCellRef.current = null;
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(draw);
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-auto"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};

export default InteractiveGridBackground;
